import * as React from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select, { SelectChangeEvent } from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import Box from '@mui/material/Box';
import RtlButton from './RtlButton';
import { useLanguage } from '../../contexts/LanguageContext';
import { cancelOrder, type CancelOrderPayload } from '../data/orders';
import { useNotification } from './CustomNotification';
interface CancelOrderDialogProps {
  open: boolean;
  orderId: number | null;
  onClose: () => void;
  onSuccess?: () => void;
}

type CancellationReasonType = 'customerRequest' | 'noAvailability' | 'paymentIssue' | 'other';

export default function CancelOrderDialog({
  open,
  orderId,
  onClose,
  onSuccess,
}: CancelOrderDialogProps) {
  const { t, direction } = useLanguage();
  const notifications = useNotification();
  const isRtl = direction === 'rtl';
  
  const [selectedReason, setSelectedReason] = React.useState<CancellationReasonType>('customerRequest');
  const [otherReasonText, setOtherReasonText] = React.useState<string>('');
  const [isSubmitting, setIsSubmitting] = React.useState(false);

  React.useEffect(() => {
    if (open) {
      // Reset form when dialog opens
      setSelectedReason('customerRequest');
      setOtherReasonText('');
    }
  }, [open]);

  const handleReasonChange = (event: SelectChangeEvent) => {
    setSelectedReason(event.target.value as CancellationReasonType);
    if (event.target.value !== 'other') {
      setOtherReasonText('');
    }
  };

  const handleOtherReasonChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setOtherReasonText(event.target.value);
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    
    if (!orderId) return;

    // Validate other reason text if "other" is selected
    if (selectedReason === 'other' && !otherReasonText.trim()) {
      notifications.show(t('orders.cancellationReasonRequired'), {
        severity: 'error',
        autoHideDuration: 3000,
      });
      return;
    }

    setIsSubmitting(true);
    try {
      const notes = selectedReason === 'other'
        ? otherReasonText.trim()
        : t(`orders.cancellationReasons.${selectedReason}`);

      const payload: CancelOrderPayload = {
        notes,
      };

      await cancelOrder(orderId, payload);

      notifications.show(t('orders.cancelSuccess'), {
        severity: 'success',
        autoHideDuration: 3000,
      });

      if (onSuccess) {
        onSuccess();
      }
      onClose();
    } catch (error) {
      notifications.show(
        `${t('messages.errorOccurred')}: ${(error as Error).message}`,
        {
          severity: 'error',
          autoHideDuration: 3000,
        },
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open && orderId !== null}
      onClose={() => !isSubmitting && onClose()}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        component: 'form',
        onSubmit: handleSubmit,
      }}
    >
      <DialogTitle>{t('orders.cancelOrderTitle')}</DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 2 }}>
          <FormControl fullWidth sx={{ mb: 3 }}>
            <InputLabel
              id="cancellation-reason-label"
              sx={{
                textAlign: isRtl ? 'right' : 'left',
                right: isRtl ? 24 : 'auto',
                left: isRtl ? 'auto' : 0,
                transformOrigin: isRtl ? 'top right' : 'top left',
              }}
            >
              {t('orders.selectCancellationReason')}
            </InputLabel>
            <Select
              labelId="cancellation-reason-label"
              id="cancellation-reason"
              value={selectedReason}
              label={t('orders.selectCancellationReason')}
              onChange={handleReasonChange}
              required
              sx={{
                textAlign: isRtl ? 'right' : 'left',
              }}
            >
              <MenuItem value="customerRequest">
                {t('orders.cancellationReasons.customerRequest')}
              </MenuItem>
              <MenuItem value="noAvailability">
                {t('orders.cancellationReasons.noAvailability')}
              </MenuItem>
              <MenuItem value="paymentIssue">
                {t('orders.cancellationReasons.paymentIssue')}
              </MenuItem>
              <MenuItem value="other">
                {t('orders.cancellationReasons.other')}
              </MenuItem>
            </Select>
          </FormControl>

          {selectedReason === 'other' && (
            <TextField
              fullWidth
              multiline
              label={t('orders.otherReason')}
              value={otherReasonText}
              onChange={handleOtherReasonChange}
              required
              InputLabelProps={{
                sx: {
                  textAlign: isRtl ? 'right' : 'left',
                  right: isRtl ? 24 : 'auto',
                  left: isRtl ? 'auto' : 0,
                  transformOrigin: isRtl ? 'top right' : 'top left',
                },
              }}
              sx={{
                '& .MuiInputBase-root': {
                  alignItems: 'flex-start',
                  height: '75px',
                  overflow: 'hidden',
                },
                '& .MuiInputBase-input': {
                  height: '75px !important',
                  overflow: 'auto !important',
                  alignItems: 'flex-start',
                  '&::-webkit-scrollbar': {
                    width: '8px',
                  },
                  '&::-webkit-scrollbar-track': {
                    background: 'rgba(0,0,0,0.1)',
                    borderRadius: '4px',
                  },
                  '&::-webkit-scrollbar-thumb': {
                    background: 'rgba(0,0,0,0.3)',
                    borderRadius: '4px',
                    '&:hover': {
                      background: 'rgba(0,0,0,0.4)',
                    },
                  },
                }
              }}
            />
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => onClose()} disabled={isSubmitting}>
          {t('common.cancel')}
        </Button>
        <RtlButton
          type="submit"
          variant="contained"
          color="error"
          loading={isSubmitting}
          sx={{ 
            background: 'red !important', 
            boxShadow: 'unset !important', 
            border: '0 !important', 
            '&:hover': { background: '#d32f2f !important' } 
          }}
        >
          {t('orders.cancelOrder')}
        </RtlButton>
      </DialogActions>
    </Dialog>
  );
}

